import React, { useEffect, useState, useRef, CSSProperties } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Image } from 'primereact/image';
import { InputSwitch } from 'primereact/inputswitch';
import ClipLoader from 'react-spinners/ClipLoader';
import NewsManagementService, { NewsManagementDTO } from '../../services/news/news-management.service';
import AddNewsModal from './add-news-modal';
import BreadCrumbCustom from '../bread-crumb/bread-crumb';
import { BreadcrumbItem } from '../../interfaces/common/common';
import RouteConstant from '../../constants/route.constants';
import './news-datatable.scss';
import AppLoading from '../app-loading/app-loading';

const NewsDataTable: React.FC = () => {
  const [newsItems, setNewsItems] = useState<NewsManagementDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsManagementDTO | null>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const draggedIndexRef = useRef<number | null>(null);
  const toast = useRef<Toast>(null);

  // Simplified breadcrumb without background image
  const breadcrumbItems: BreadcrumbItem = {
    items: [{ label: 'News Management' }],
    home: { label: 'Home' },
    backgroundImage: '',
    title: 'News Management',
  };

  // Fetch all news on component mount
  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const data = await NewsManagementService.getAllNews();
      setNewsItems(data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
      showMessage('error', 'Error', 'Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (severity: 'success' | 'error' | 'info', summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleEditNews = (news: NewsManagementDTO) => {
    setEditingNews(news);
    setShowAddModal(true);
  };


  const handleStatusToggle = async (rowData: NewsManagementDTO) => {
  try {
    const newsId = rowData.ID;
    setNewsItems((prev) =>
  prev.map((item) =>
    (item.ID === newsId || item.ID=== newsId)
      ? { ...item, isActive: !item.isActive }
      : item
  )
);


    const res = await NewsManagementService.deleteNews(newsId as number);

    if (!res.isSuccess) {
      showMessage('error', 'Error', res.message);
      fetchNews(); 
       } else {
      showMessage('success', 'Success', res.message);
        }
      } catch (error) {
    showMessage('error', 'Error', 'Status update failed');
    fetchNews();
    }
  };

  const handleModalSuccess = () => {
    setEditingNews(null);
    fetchNews();
  };

  const handleAddNews = () => {
    setEditingNews(null);
    setShowAddModal(true);
  };

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const getDisplayData = () => {
    return newsItems.slice(first, first + rows);
  };

  // Helper for Time Ago
  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d old`;
  };

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    draggedIndexRef.current = index;
    e.dataTransfer!.effectAllowed = 'move';
    // Set data with the actual index
    e.dataTransfer!.setData('text/html', String(index));
    setTimeout(() => setDraggedIndex(index), 0);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    if (draggedIndexRef.current !== null && draggedIndexRef.current !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDropTargetIndex(null);
  };

  const handleDragEnd = () => {
    draggedIndexRef.current = null;
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    const draggedIdx = draggedIndexRef.current;
    
    // Clean up immediately
    draggedIndexRef.current = null;
    setDraggedIndex(null);
    setDropTargetIndex(null);
    
    // Validate drop
    if (draggedIdx === null || draggedIdx === dropIndex) {
      return;
    }

    // Create a copy and swap
    const updated = [...newsItems];
    [updated[draggedIdx], updated[dropIndex]] = [updated[dropIndex], updated[draggedIdx]];
    setNewsItems(updated);

    try {
      // Send update to server
      const orderPayload = updated.map((item, idx) => ({
        Id: item.ID!,
        DisplayOrder: idx + 1
      }));
      
      await NewsManagementService.updateNewsOrder(orderPayload);
      showMessage('success', 'Success', 'News swapped successfully');
    } catch {
      showMessage('error', 'Error', 'Failed to swap news');
      fetchNews();
    }
  };

  // NewsCard Component
  const NewsCard = ({ item, index }: { item: NewsManagementDTO; index: number }) => {
    const createdBy = item.modifiedByName || item.createdByName || 'Admin';
    const createdByInitial = createdBy.charAt(0).toUpperCase();
    const dateVal = item.modifiedDate || item.createdDate;
    const statusClass = item.isActive ? 'status-active' : 'status-inactive';
    const isDragging = draggedIndex === index;
    const isDropTarget = dropTargetIndex === index;

    return (
      <div
        className={`news-card ${statusClass} ${isDragging ? 'dragging' : ''} ${isDropTarget && draggedIndex !== index ? 'drop-target' : ''}`}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragEnter={handleDragEnter}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragLeave={handleDragLeave}
        onDragEnd={handleDragEnd}
        onDrop={(e) => handleDrop(e, index)}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: 'grab'
        }}
      >
        <div className="card-icon-box drag-handle">
          {item.imageUrl ? (
            <Image
              src={item.imageUrl}
              alt={item.title}
              preview
              width="56"
              height="56"
              style={{ borderRadius: '8px' }}
            />
          ) : (
            <div className="no-image-placeholder">
              <i className="pi pi-image"></i>
            </div>
          )}
        </div>

        <div className="card-content">
          <div className="card-header">
            <h3 className="card-title">{item.title}</h3>
          </div>
          <p className="card-description">
            {(item.newsDescription || item.description || '').substring(0, 80)}...
          </p>
          <div className="card-meta">
            <div className="meta-item">
              <i className="pi pi-user"></i>
              <span>{createdBy}</span>
            </div>
            <div className="meta-item">
              <i className="pi pi-calendar"></i>
              <span>{dateVal ? timeAgo(dateVal) : '-'}</span>
            </div>
          </div>
        </div>

        <div className="card-actions">
          <span className={`status-badge-modern ${item.isActive ? 'active' : 'inactive'}`}>
            {item.isActive ? 'Active' : 'Inactive'}
          </span>
          <div className="flex gap-1">
            <InputSwitch
              checked={item.isActive}
              onChange={(e) => {
                e.preventDefault?.();
                e.stopPropagation?.();
                handleStatusToggle(item);
              }}
              onClick={(e: any) => {
                e.preventDefault?.();
                e.stopPropagation?.();
              }}
              onMouseDown={(e: any) => {
                e.stopPropagation?.();
              }}
              className="status-toggle-switch"
            />
            {item.isActive && (
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text action-btn-modern"
                onClick={(e: any) => {
                  e.preventDefault?.();
                  e.stopPropagation?.();
                  handleEditNews(item);
                }}
                onMouseDown={(e: any) => {
                  e.stopPropagation?.();
                }}
                tooltip="Edit"
              />
            )}
          </div>
        </div>
      </div>
    );
  };
  

  // Column templates
  const imageTemplate = (rowData: NewsManagementDTO) => {
    return rowData.imageUrl ? (
      <Image
        src={rowData.imageUrl}
        alt={rowData.title}
        preview
        width="80"
        height="80"
        style={{ borderRadius: '6px' }}
      />
    ) : (
      <span className="text-gray-400">No Image</span>
    );
  };

  const loaderStyle: CSSProperties = {
    display: 'block',
    margin: '0 auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };

  return (
    <>
      <div className="news-datatable-container">
        {loading ? (
          <ClipLoader cssOverride={loaderStyle} size={50} color="#90ee90" loading={loading} />
        ) : (
          <>
            <Toast ref={toast} />

            <div className="col-12">
              <div className="flex flex-column gap-3">
                {/* Header Section with Info */}
                <div className="card p-4 w-full" style={{
                    backgroundColor: '#e8f5e9',
                    borderRadius: '12px'
                  }}>
                    {/* Header Section with gradient - nested inside outer container */}
                    <div className="flex align-items-center gap-3 mb-4" style={{
                      backgroundImage: 'linear-gradient(to right, white, rgb(232, 245, 233))',
                      padding: '16px',
                      borderRadius: '8px',
                      marginLeft: '-16px',
                      marginRight: '-16px',
                      marginTop: '-16px',
                      paddingLeft: '16px',
                      paddingRight: '16px'
                    }}>
                      <div style={{
                        backgroundColor: 'rgb(76, 175, 80)',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '48px',
                        height: '48px'
                      }}>
                        <i className="pi pi-book" style={{ color: 'white', fontSize: '1.5rem' }}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5 className="m-0 mb-1" style={{ color: '#2d3748', fontWeight: '600', fontSize: '1.125rem' }}>
                          All News
                        </h5>
                        <p className="m-0" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          Manage and publish news articles
                        </p>
                        {/* News Count */}
                        <div className="flex align-items-center gap-2 mt-2">
                          <i className="pi pi-chart-bar" style={{ fontSize: '0.875rem', color: 'rgb(76, 175, 80)' }}></i>
                          <span style={{ fontSize: '0.875rem', color: '#2d3748' }}>
                            <span className="font-bold">{newsItems.length}</span> Total news 
                          </span>
                        </div>
                      </div>

                      <div style={{ flexShrink: 0 }}>
                        <Button
                          label="Add News"
                          icon="pi pi-plus"
                          onClick={handleAddNews}
                          className="p-button-success"
                        />
                      </div>
                    </div>
                  </div>

                {/* News Card List */}
                <div className="results-section">
                  {newsItems.length === 0 && !loading ? (
                    <div className="flex flex-column align-items-center justify-content-center p-6 bg-white border-round-xl border-1 border-gray-200">
                      <div className="bg-green-50 p-4 border-circle mb-3">
                        <i className="pi pi-inbox text-4xl text-green-500"></i>
                      </div>
                      <span className="text-xl font-semibold text-gray-700">No news found</span>
                      <p className="text-gray-500 mt-2">Start by adding a new news article</p>
                    </div>
                  ) : (
                    <div className="news-card-list">
                      {getDisplayData().map((item: NewsManagementDTO, index: number) => (
                        <NewsCard key={item.ID} item={item} index={first + index} />
                      ))}
                    </div>
                  )}

                  <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={newsItems.length}
                    rowsPerPageOptions={[10, 20, 30 ,40 ,50]}
                    onPageChange={onPageChange}
                    className="custom-paginator mt-4"
                  />
                </div>
              </div>
            </div>
        </>
        )}

      {/* Add/Edit News Modal */}
      <AddNewsModal
        visible={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setEditingNews(null);
        }}
        onSuccess={handleModalSuccess}
        editingNews={editingNews}
      />
      </div>
    </>
  );
};

export default NewsDataTable;
